/**
 * ProtonWebSDK wallet connection manager.
 *
 * Handles connect, disconnect, and session restoration via WebAuth.
 */

import { CHAIN_ID, APP_IDENTIFIER } from './constants.js';
import { getApiUrl } from './rpc.js';
import { log } from './logger.js';
import ConnectWallet from '@proton/web-sdk';

let link = null;
let session = null;

/** Shared SDK options (without restoreSession). */
function sdkOptions(restoreSession) {
  return {
    linkOptions: {
      endpoints: [getApiUrl()],
      chainId: CHAIN_ID,
      restoreSession,
    },
    transportOptions: {
      requestAccount: APP_IDENTIFIER,
      backButton: true,
    },
    selectorOptions: {
      appName: 'XPR-MSIG-APP',
      appLogo: '',
      customStyleOptions: {
        modalBackgroundColor: '#F9FAFB',
        logoBackgroundColor: '#7B2FBE',
        optionBackgroundColor: 'white',
        optionFontColor: '#1F2937',
        primaryFontColor: '#1F2937',
        secondaryFontColor: '#6B7280',
        linkColor: '#7B2FBE',
      },
    },
  };
}

/**
 * Try to restore a previously saved session silently (no UI).
 * Returns { actor, permission } on success, or null if no session to restore.
 */
export async function restoreSession() {
  try {
    const result = await ConnectWallet(sdkOptions(true));

    if (result.error) return null;
    if (!result.session || !result.session.auth) return null;

    link = result.link;
    session = result.session;

    const actor = session.auth.actor.toString();
    const permission = session.auth.permission.toString();
    log(`Session restored: ${actor}@${permission}`, 'success');
    return { actor, permission };
  } catch {
    return null;
  }
}

/**
 * Connect to a WebAuth wallet using ProtonWebSDK.
 * Shows the wallet selector modal for the user to pick a wallet type.
 *
 * @returns {Promise<{actor: string, permission: string}>}
 * @throws {Error} if connection fails
 */
export async function connectWallet() {
  log('Initiating WebAuth wallet connection...');

  const result = await ConnectWallet(sdkOptions(false));

  if (result.error) {
    throw typeof result.error === 'string'
      ? new Error(result.error)
      : result.error;
  }

  if (!result.session || !result.session.auth) {
    throw new Error('No session returned from wallet. Please try again.');
  }

  link = result.link;
  session = result.session;

  const actor = session.auth.actor.toString();
  const permission = session.auth.permission.toString();

  log(`Wallet connected: ${actor}@${permission}`, 'success');
  return { actor, permission };
}

/**
 * Disconnect the current wallet session.
 */
export async function disconnectWallet() {
  if (link && session) {
    await link.removeSession(APP_IDENTIFIER, session.auth);
  }
  link = null;
  session = null;
  log('Wallet disconnected.');
}

/**
 * Get the current session (or null if not connected).
 * @returns {object|null}
 */
export function getSession() {
  return session;
}

/**
 * Check if a wallet is currently connected.
 * @returns {boolean}
 */
export function isConnected() {
  return session !== null;
}